import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {
  HttpAlreadyPurchaseError,
  HttpConflictError,
  HttpErrorMessageCode,
  HttpMiscellaneousError,
  HttpNoConnectionError,
  HttpNotFoundError,
  HttpPaymentThreeDSRequired,
  HttpServerError,
  HttpUnprocessableEntityError,
  NibTravelHttpError,
} from '@nibtravel/shared/util/models';
import { LogEntry, LogLevel } from './models/logger.models';

@Injectable({ providedIn: 'root' })
export class LogFormatter {
  public format(
    logLevel: LogLevel,
    logEntry: LogEntry<any>,
    brandCode: string
  ): any {
    if (logLevel === LogLevel.Error) {
      return this.formatError(logEntry, brandCode);
    }

    return this.formatInfoLog(logEntry, brandCode, logLevel);
  }

  /** Format the error message for logging */
  private formatError(
    logEntry: LogEntry<unknown>,
    brand: string
  ): Record<string, unknown> {
    let message, stackTrace;

    const metaData = {
      logLevel: LogLevel.Error.toString(),
      ...this.getBrowserInfo(),
      ...(logEntry.extraProps || {}),
      brand,
      category: logEntry.category,
      method: logEntry.method,
      context: logEntry.context,
      correlationId: logEntry.correlationId,
    };

    if (logEntry.value instanceof Error) {
      message = this.getClientErrorMessage(logEntry.value);
      stackTrace = this.parseStack(logEntry.value.stack);

      return {
        ...metaData,
        message,
        stackTrace,
      };
    } else if (logEntry.value instanceof NibTravelHttpError) {
      message = this.getServerMessage(logEntry.value);

      return {
        ...metaData,
        message,
        url: logEntry.value.response?.url,
        validation: (logEntry.value as HttpUnprocessableEntityError).validation,
        httpStatus: logEntry.value.response?.status,
      };
    } else if (typeof logEntry.value === 'string') {
      return {
        ...metaData,
        message: logEntry.value,
      };
    }

    return metaData;
  }

  /** Format the error message for logging */
  private formatInfoLog(
    logEntry: LogEntry<any>,
    brand: string,
    logLevel: LogLevel
  ): Record<string, unknown> {
    const metaData = {
      logLevel: logLevel ? logLevel.toString() : LogLevel.Info.toString(),
      ...this.getBrowserInfo(),
      ...(logEntry.extraProps || {}),
      brand,
      category: logEntry.category,
      method: logEntry.method,
      context: logEntry.context,
      correlationId: logEntry.correlationId,
    };

    return typeof logEntry.value === 'string'
      ? {
          ...metaData,
          message: logEntry.value,
        }
      : {
          ...metaData,
          ...{ data: logEntry.value },
        };
  }

  private getBrowserInfo(): any {
    return {
      href: window.location.href, // This will not work with SSR
      userAgent: window.navigator.userAgent, // This will not work with SSR
    };
  }

  /** Try to get the server messages based on a specific Http Error */
  private tryGetServerMessage(
    error?: NibTravelHttpError | HttpErrorResponse
  ): string | undefined {
    if (!error) {
      return;
    }
    if (error instanceof HttpNoConnectionError) {
      return HttpErrorMessageCode.NoInternet;
    }
    if (error instanceof HttpUnprocessableEntityError) {
      return HttpErrorMessageCode.InvalidInputFallback;
    }
    if (error instanceof HttpNotFoundError) {
      return HttpErrorMessageCode.NotFound;
    }
    if (
      error instanceof HttpServerError ||
      error instanceof HttpConflictError ||
      error instanceof HttpMiscellaneousError
    ) {
      return HttpErrorMessageCode.ServerError;
    }

    if (error instanceof HttpPaymentThreeDSRequired) {
      return HttpErrorMessageCode.PaymentRequiredError;
    }

    if (error instanceof HttpAlreadyPurchaseError) {
      return HttpErrorMessageCode.AlreadyPurchased;
    }
    if (error instanceof HttpErrorResponse) {
      return error.message;
    }
    return error.response?.error;
  }

  /** A wrapper to get the message from a server error with a fallback */
  private getServerMessage(
    error?: NibTravelHttpError,
    fallback?: string
  ): string {
    const message = this.tryGetServerMessage(error);
    return message || fallback || HttpErrorMessageCode.ServerError;
  }

  private parseStack(stack: string | undefined): any {
    return stack ? stack.split('\n') : stack;
  }

  /** Format the error message coming from client side issues */
  private getClientErrorMessage(error: Error) {
    return error.message ? error.message : error.toString();
  }
}
